import ClassNames from "classnames";
import { EmblaCarouselType, EmblaOptionsType } from "embla-carousel";
import Fade from "embla-carousel-fade";
import useEmblaCarousel from "embla-carousel-react";
import { Children, FunctionComponent, ReactNode, useEffect } from "react";
import styles from "../stylesheets/components/slider.module.css";
import { SliderAlign, SliderEffect } from "../types/index.js";
import { useDidMountEffect } from "../utils/hooks.js";

interface Props {
  slidesToShow: number;
  className?: string;
  containerClassName?: string;
  slideClassName?: string;
  onSliderLoaded: (slider: EmblaCarouselType) => void;
  sliderEffect: SliderEffect;
  isPreview: boolean;
  loop?: boolean;
  align?: SliderAlign;
  slideIds: string[];
  activePreviewSlideId: string | undefined;
  children: ReactNode;
}

const EmblaCarouselSlide: FunctionComponent<{
  children: ReactNode;
  className: string | undefined;
  slidesToShow: number;
}> = ({ children, className, slidesToShow }) => (
  <div
    className={ClassNames(styles.EmblaCarousel__Slide, className, {
      [styles["EmblaCarousel__Slide--half"]]: slidesToShow === 2,
      [styles["EmblaCarousel__Slide--third"]]: slidesToShow === 3,
      [styles["EmblaCarousel__Slide--quarter"]]: slidesToShow === 4,
      [styles["EmblaCarousel__Slide--fifth"]]: slidesToShow === 5,
      [styles["EmblaCarousel__Slide--sixth"]]: slidesToShow === 6,
    })}
  >
    {children}
  </div>
);

const isServer = typeof navigator === "undefined";

const Slider: FunctionComponent<Props> = ({
  children,
  className,
  containerClassName,
  slideClassName,
  onSliderLoaded,
  slidesToShow: slidesToShowProp,
  sliderEffect,
  isPreview,
  activePreviewSlideId,
  slideIds,
  loop = true,
  align = "start",
}) => {
  const childrenArray = Children.toArray(children);

  const isSlide = sliderEffect === "slide";
  const options: EmblaOptionsType = {
    loop,
    align,
    skipSnaps: isSlide,
    watchDrag: !isPreview,
  };
  const slidesToShow = isSlide ? slidesToShowProp : 1;

  const [emblaRef, emblaInstance] = useEmblaCarousel(
    options,
    !isSlide ? [Fade()] : [],
  );

  useDidMountEffect(() => {
    emblaInstance?.reInit(options);
  }, [slidesToShow, childrenArray.length]);

  useDidMountEffect(() => {
    emblaInstance?.reInit({
      ...options,
      loop: isSlide && loop,
      skipSnaps: isSlide,
    });
  }, [sliderEffect]);

  useEffect(() => {
    if (!emblaInstance) return;
    onSliderLoaded(emblaInstance);
  }, [emblaInstance]);

  // Slide to active module in preview
  useEffect(() => {
    if (!activePreviewSlideId || !emblaInstance) return;
    const index = slideIds.indexOf(activePreviewSlideId);
    index >= 0 && emblaInstance.scrollTo(index);
  }, [activePreviewSlideId, emblaInstance]);

  const carouselClassNames = ClassNames(styles.EmblaCarousel, className);
  const containerClassNames = ClassNames(
    styles.EmblaCarousel__Container,
    containerClassName,
  );

  return isServer ? (
    <div className={carouselClassNames}>
      <div className={containerClassNames}>
        {childrenArray
          .map((child, index) => (
            <EmblaCarouselSlide
              key={index}
              className={slideClassName}
              slidesToShow={slidesToShow}
            >
              {child}
            </EmblaCarouselSlide>
          ))
          .slice(0, slidesToShow)}
      </div>
    </div>
  ) : (
    <div ref={emblaRef} className={carouselClassNames}>
      <div className={containerClassNames}>
        {childrenArray.map((child, index) => (
          <EmblaCarouselSlide
            key={index}
            className={slideClassName}
            slidesToShow={slidesToShow}
          >
            {child}
          </EmblaCarouselSlide>
        ))}
      </div>
    </div>
  );
};

export default Slider;
